import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Users, 
  Clock, 
  Award, 
  Star, 
  CheckCircle, 
  BookOpen, 
  Globe,
  Mail,
  ArrowRight,
  Target,
  Briefcase,
  TrendingUp,
  Code,
  ExternalLink,
  Home,
  ArrowLeft
} from "lucide-react";

const CSP = () => {
  const keyAreas = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Strategic Planning",
      description: "Executive-level decision-making and enterprise-wide security strategy development"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Cross-Functional Leadership",
      description: "Leading diverse teams across IT, OT, compliance, and physical security domains"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Governance & Risk Management",
      description: "Advanced governance frameworks and comprehensive risk assessment methodologies"
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: "Stakeholder Engagement",
      description: "Executive communication and stakeholder management in complex security environments"
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Scenario-Based Workshops",
      description: "Practical tabletop exercises and real-world security scenario planning"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Compliance Integration",
      description: "Advanced compliance strategies across multiple regulatory frameworks"
    }
  ];

  const benefits = [
    "Strategic insight for enterprise-wide security initiatives",
    "Operational leadership skills for cross-functional teams",
    "Executive-level decision-making capabilities",
    "Advanced governance and risk management expertise",
    "Practical experience through scenario-based learning",
    "Industry recognition for strategic security leadership"
  ];

  const idealFor = [
    "CSCE certified professionals ready for leadership roles",
    "Security managers overseeing enterprise environments",
    "IT leaders expanding into converged security strategy",
    "Risk management professionals seeking advanced skills",
    "Compliance officers managing complex security frameworks",
    "Senior professionals driving security transformation"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-16 relative">
        {/* Navigation Bar - Upper Right */}
        <div className="absolute top-4 right-6 flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            className="bg-white/10 text-white border-white/20 hover:bg-white/20"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            className="bg-white/10 text-white border-white/20 hover:bg-white/20"
            onClick={() => window.open('https://convergedsecurity.es/', '_blank')}
          >
            <Home className="w-4 h-4 mr-1" />
            Home
          </Button>
        </div>
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-white/20 text-white border-white/30 mb-4">
                Advanced Certification
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                CSP
              </h1>
              <h2 className="text-2xl md:text-3xl font-semibold mb-4 text-blue-100">
                Converged Security Professional
              </h2>
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                Advanced credential for strategic insight and operational leadership. Lead cross-functional teams, drive enterprise-wide security initiatives, and influence executive-level decision-making.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="bg-white text-blue-600 hover:bg-blue-50"
                  onClick={() => window.open('mailto:info@convergedsecurity.es?subject=CSP Certification Application&body=Hello, I would like to apply for the CSP (Converged Security Professional) certification program. Please send me the application details and next steps.', '_blank')}
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Apply Now
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/10"
                  onClick={() => window.open('https://pay.sumup.com/b2c/QH1JMRP5', '_blank')}
                >
                  <Star className="w-5 h-5 mr-2" />
                  Pay Now - €750
                </Button>
              </div>
              <div className="flex flex-wrap gap-3 mt-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                  onClick={() => window.open('/#/csp-embed-docs', '_blank')}
                >
                  <Code className="w-4 h-4 mr-2" />
                  Get Embed Code
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                  onClick={() => window.open('/#/csp-embed', '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Preview Embed
                </Button>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <img 
                  src="./images/csp.png" 
                  alt="CSP Certification Badge"
                  className="w-80 h-80 object-contain drop-shadow-2xl"
                />
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-400/20 to-indigo-400/20 rounded-full blur-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Facts */}
      <div className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-6">
            <Card className="text-center border-blue-200">
              <CardContent className="pt-6">
                <Star className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <h3 className="font-bold text-2xl text-gray-900">€750</h3>
                <p className="text-gray-600">Per Student</p>
              </CardContent>
            </Card>
            <Card className="text-center border-blue-200">
              <CardContent className="pt-6">
                <Clock className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <h3 className="font-bold text-2xl text-gray-900">3 Years</h3>
                <p className="text-gray-600">Validity Period</p>
              </CardContent>
            </Card>
            <Card className="text-center border-blue-200">
              <CardContent className="pt-6">
                <Users className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <h3 className="font-bold text-2xl text-gray-900">CSCE Required</h3>
                <p className="text-gray-600">Prerequisite</p>
              </CardContent>
            </Card>
            <Card className="text-center border-blue-200">
              <CardContent className="pt-6">
                <Award className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <h3 className="font-bold text-2xl text-gray-900">On-Site</h3>
                <p className="text-gray-600">Small Group Format</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Key Learning Areas */}
      <div className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Strategic Leadership Development
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Advanced curriculum focused on strategic planning, operational leadership, and executive-level decision-making
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {keyAreas.map((area, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300 border-blue-100">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                      {area.icon}
                    </div>
                    <CardTitle className="text-lg">{area.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{area.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Program Benefits */}
      <div className="py-16 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Why Choose CSP?
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                The CSP certification elevates CSCE professionals to strategic leadership roles in converged security.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-blue-200 flex-shrink-0 mt-0.5" />
                    <p className="text-blue-100">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6">Program Highlights</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-blue-200" />
                  <span>Small group format for personalized attention</span>
                </div>
                <div className="flex items-center gap-3">
                  <Target className="w-5 h-5 text-blue-200" />
                  <span>Scenario-based workshops and tabletop exercises</span>
                </div>
                <div className="flex items-center gap-3">
                  <Briefcase className="w-5 h-5 text-blue-200" />
                  <span>Final group presentation assessment</span>
                </div>
                <div className="flex items-center gap-3">
                  <BookOpen className="w-5 h-5 text-blue-200" />
                  <span>Led by Dr. Vladimir Bunic, PhD in Cybersecurity</span>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-blue-200" />
                  <span>Digital certificate and badge, verifiable online</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ideal Candidates */}
      <div className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Perfect For
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              CSP is designed for CSCE-certified professionals ready to advance to strategic leadership positions
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {idealFor.map((candidate, index) => (
              <Card key={index} className="border-blue-100 hover:border-blue-300 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">{candidate}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Assessment Information */}
      <div className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Assessment & Certification
            </h2>
            <p className="text-xl text-gray-600">
              Performance evaluated through active participation and collaborative learning
            </p>
          </div>
          
          <Card className="border-blue-200 shadow-lg">
            <CardContent className="pt-8">
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-bold mb-4 text-blue-600">Assessment Method</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600" />
                      <span>Active participation in workshops</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600" />
                      <span>Collaborative scenario exercises</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600" />
                      <span>Final group presentation</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600" />
                      <span>Peer collaboration assessment</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-4 text-blue-600">Key Features</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Star className="w-5 h-5 text-blue-600" />
                      <span>No additional written exam required</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Star className="w-5 h-5 text-blue-600" />
                      <span>Focus on practical application</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Star className="w-5 h-5 text-blue-600" />
                      <span>Real-world scenario planning</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Star className="w-5 h-5 text-blue-600" />
                      <span>Executive-level skill development</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Next Steps */}
      <div className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Ready to Lead?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Take your CSCE certification to the next level with strategic leadership training
          </p>
          
          <Card className="border-blue-200 shadow-lg">
            <CardContent className="pt-8">
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <h3 className="font-semibold mb-2">Verify CSCE</h3>
                  <p className="text-sm text-gray-600">Ensure your CSCE certification is current and valid</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-blue-600 font-bold">2</span>
                  </div>
                  <h3 className="font-semibold mb-2">Apply & Attend</h3>
                  <p className="text-sm text-gray-600">Join the on-site strategic training program</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-blue-600 font-bold">3</span>
                  </div>
                  <h3 className="font-semibold mb-2">Lead & Certified</h3>
                  <p className="text-sm text-gray-600">Complete program and receive CSP certification</p>
                </div>
              </div>
              
              <div className="flex flex-wrap justify-center gap-4">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  onClick={() => window.open('https://pay.sumup.com/b2c/QH1JMRP5', '_blank')}
                >
                  <Star className="w-5 h-5 mr-2" />
                  Pay Now - €750
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-blue-300 text-blue-600 hover:bg-blue-50"
                  onClick={() => window.open('mailto:info@convergedsecurity.es?subject=CSP Certification Application&body=Hello, I would like to apply for the CSP (Converged Security Professional) certification program. I am CSCE certified and ready for strategic leadership training. Please send me the application details and next steps.', '_blank')}
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Contact Us
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-blue-300 text-blue-600 hover:bg-blue-50"
                  onClick={() => window.open('mailto:info@convergedsecurity.es?subject=CSP Brochure Request&body=Hello, I would like to download the CSP (Converged Security Professional) certification brochure. Please send me the detailed program information.', '_blank')}
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  Download Brochure
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white py-8">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-gray-400 mb-2">
            © 2025 Converged Security Institute (CSI). All rights reserved.
          </p>
          <p className="text-sm text-gray-500">
            CSP certification adheres to European Union GDPR, Cybersecurity Act, and NIS Directive
          </p>
        </div>
      </div>
    </div>
  );
};

export default CSP;